/**
 * Cableado de Field_Ops al runtime selector y derivación de sus insumos
 * desde los JSONs reales de Antofagasta (tarea 9.1).
 *
 * Este módulo es el **puente** entre las vistas del SPA (Crews, Inspector)
 * y la implementación client-side de Field_Ops (`./fieldOps`).
 * Responsabilidades:
 *
 *   1. **Registrar** la implementación DEMO del servicio `fieldOps` en el
 *      runtime selector (`../api/runtime`) de forma idempotente, de modo
 *      que las vistas lo consuman SIEMPRE vía `runtime.fieldOps()`
 *      (R17.1, R17.2, R17.5) y nunca instancien Field_Ops directamente.
 *      Comparte el mismo {@link EventStore} demo que el resto de la Capa
 *      para que el registro de evidencia (R8.9) extienda la misma cadena
 *      hash de auditoría (R9).
 *
 *   2. **Sembrar** Cuadrillas y Ordenes_Inspeccion sintéticas, derivadas de
 *      `dmas.json` (sin duplicar entidades de la fundación, R17), para que
 *      `track`, `route` y `associateEvidence` operen sobre ubicaciones
 *      reales de la red.
 *
 *   3. **Exponer** loaders (`loadCrewsBundle`, `crewRoute`) que las vistas
 *      usan en sus `queryFn`/handlers a través del contrato.
 */

import type { Coordenada, EntidadRef, Id, RutaOptimizada } from "../api/contracts";
import { loadRealData } from "../api/realData";
import { provideDemo, runtime } from "../api/runtime";

import {
  FieldOpsLocal,
  type FieldOpsLocalOptions,
  type OrdenUbicada,
} from "./fieldOps";

/** Singleton de Field_Ops demo, compartido por las vistas y el seed. */
let fieldOps: FieldOpsLocal | null = null;
let registrado = false;

/** Devuelve (creándolo si hace falta) el Field_Ops demo cableado al Event_Store. */
function instancia(opts?: FieldOpsLocalOptions): FieldOpsLocal {
  if (!fieldOps) {
    fieldOps = new FieldOpsLocal({ eventStore: runtime.eventStore(), ...opts });
  }
  return fieldOps;
}

/**
 * Registra (una sola vez) la implementación DEMO de Field_Ops en el runtime
 * selector. Idempotente: invocarlo varias veces no recrea el registro.
 */
export function registerFieldOpsRuntime(): void {
  if (registrado) return;
  registrado = true;
  provideDemo("fieldOps", () => instancia());
}

// Registro temprano para que las llamadas del contrato resuelvan al demo y
// no al stub "no implementado".
registerFieldOpsRuntime();

// ---------------------------------------------------------------------------
// Derivación de Cuadrillas y Ordenes sintéticas desde los JSONs reales (R17)
// ---------------------------------------------------------------------------

/** Pequeño desplazamiento determinista alrededor del centroide de la DMA. */
function offset(base: Coordenada, k: number): Coordenada {
  // Patrón determinista (no aleatorio) para distribuir órdenes por DMA.
  const dx = ((k % 3) - 1) * 0.004;
  const dy = ((Math.floor(k / 3) % 3) - 1) * 0.004;
  return { lon: base.lon + dx, lat: base.lat + dy };
}

/** Coordenada base de una DMA real (centroide aproximado de Antofagasta). */
function coordDma(i: number): Coordenada {
  // Los JSONs reales no siempre traen geometría; se distribuyen de forma
  // determinista alrededor del centro de la red para el modo demo.
  const baseLon = -70.4 + ((i % 8) - 4) * 0.01;
  const baseLat = -23.65 + ((Math.floor(i / 8) % 8) - 4) * 0.01;
  return { lon: baseLon, lat: baseLat };
}

/** Cuadrilla sintética derivada para la vista Crews. */
export interface CuadrillaDemo {
  cuadrilla_id: Id;
  dma_ref: EntidadRef;
  coord: Coordenada;
  ordenes: OrdenUbicada[];
}

/**
 * Siembra Field_Ops con Cuadrillas y Ordenes derivadas de las primeras DMAs
 * reales y devuelve la lista para que las vistas la muestren. Determinista.
 */
export async function seedFieldOps(maxCuadrillas = 6): Promise<CuadrillaDemo[]> {
  const fo = instancia();
  const data = await loadRealData();
  const dmas = data?.dmas ?? [];
  const cuadrillas: CuadrillaDemo[] = [];

  const n = Math.min(maxCuadrillas, dmas.length || maxCuadrillas);
  for (let i = 0; i < n; i++) {
    const dma = dmas[i];
    const dmaRef: EntidadRef = { tipo: "DMA", id: dma?.codigo ?? `DMA-${i + 1}` };
    const base = dma ? coordDma(i) : { lon: -70.4, lat: -23.65 };
    const cuadrilla_id = `CUAD-${String(i + 1).padStart(2, "0")}`;

    // 2..4 órdenes deterministas por cuadrilla para ejercitar la optimización.
    const nOrdenes = 2 + (i % 3);
    const ordenes: OrdenUbicada[] = [];
    for (let k = 0; k < nOrdenes; k++) {
      const orden_ref: EntidadRef = { tipo: "Orden_Inspeccion", id: `${cuadrilla_id}-ORD-${k + 1}` };
      const orden: OrdenUbicada = {
        orden_ref,
        coord: offset(base, k),
        identificador: `MED-${cuadrilla_id}-${k + 1}`,
      };
      ordenes.push(orden);
      await fo.setOrden(orden);
    }

    fo.setCuadrilla(cuadrilla_id, base, "en_ruta");
    cuadrillas.push({ cuadrilla_id, dma_ref: dmaRef, coord: base, ordenes });
  }

  return cuadrillas;
}

/**
 * Calcula la ruta optimizada de una Cuadrilla **a través del runtime
 * selector** (`runtime.fieldOps().route`) (R8.4). Devuelve `null` si el
 * servicio no puede calcularla.
 */
export async function crewRoute(cuadrilla_id: Id, ordenes: EntidadRef[]): Promise<RutaOptimizada | null> {
  const res = await runtime.fieldOps().route(cuadrilla_id, ordenes);
  return res.ok ? res.value : null;
}

/**
 * Registra una Orden_Inspeccion en el almacén offline de Field_Ops para que
 * pueda consultarse y asociarle evidencia NFC/QR sin conectividad (R8.5).
 * Usado por la vista Inspector al abrir una orden.
 */
export async function registrarOrdenOffline(orden: OrdenUbicada): Promise<void> {
  await instancia().setOrden(orden);
}
